import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Contact', path: '/contact' },
    { name: 'Pay Online', link: 'https://buy.stripe.com/3csbJY9FxcyJcgw4gg' }, 
  ];

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed w-full top-0 z-50 transition-all duration-300`}
      style={{
        background: scrolled 
          ? 'linear-gradient(135deg, rgb(254, 240, 138) 0%, rgb(180, 134, 59) 50%, rgb(59, 130, 246) 100%)'
          : 'linear-gradient(135deg, rgb(254, 240, 138) 0%, rgb(180, 134, 59) 50%, rgb(59, 130, 246) 100%)'
      }}
    >
      <div className="container mx-auto px-6 py-5 flex justify-between items-center">
        {/* Logo with Ascendant */}
        <Link to="/" className="flex items-center" onClick={scrollToTop}>
          <div className="h-20 flex items-center">
            <img 
              src="/assets/images/Logo.png" 
              alt="Structural Solutions Logo" 
              className="h-20 w-auto"
            />
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => (
            item.link ? (
              <a
                key={item.name}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-lg font-medium relative py-2 transition-all duration-300 group ${
                  scrolled ? 'text-gray-800' : 'text-gray-800'
                }`}
              >
                {item.name}
                <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ) : (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `text-lg font-medium relative py-2 transition-all duration-300 group ${
                    scrolled ? 'text-gray-800' : 'text-gray-800'
                  } ${isActive ? 'font-semibold' : ''}`
                }
                onClick={scrollToTop}
              >
                {item.name}
                <span className={`absolute left-0 bottom-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full ${
                  ({ isActive }) => isActive ? 'w-full' : 'w-0'
                }`}></span>
              </NavLink>
            )
          ))}
          {/* <Link 
            to="/contact" 
            className="ml-4 px-6 py-2.5 rounded-full font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-300"
            onClick={scrollToTop}
          >
            Get Started
          </Link> */}
        </nav>
        
        {/* Mobile menu button */}
        <button
          className="md:hidden"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? 
            <X className="h-8 w-8 text-gray-800" /> : 
            <Menu className="h-8 w-8 text-blue-600" />
          }
        </button>
      </div>
      
      {/* Mobile menu */}
      {isOpen && (
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="md:hidden shadow-xl"
          style={{
            background: 'linear-gradient(135deg, rgb(254, 240, 138) 0%, rgb(180, 134, 59) 50%, rgb(59, 130, 246) 100%)'
          }}
        >
          <div className="container mx-auto py-4">
            {navItems.map((item) => (
              item.link ? (
                <a
                  key={item.name}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-4 px-6 text-lg border-b border-gray-100 text-gray-700 hover:text-blue-600"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  {item.name}
                </a>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `block py-4 px-6 text-lg border-b border-gray-100 ${
                      isActive
                        ? 'text-blue-600 font-semibold'
                        : 'text-gray-700 hover:text-blue-600'
                    }`
                  }
                  onClick={() => {
                    setIsOpen(false);
                    scrollToTop();
                  }}
                >
                  {item.name}
                </NavLink>
              )
            ))}
            <div className="px-6 py-4">
              <Link 
                to="/contact" 
                className="block w-full text-center py-3 px-6 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                onClick={() => {
                  setIsOpen(false);
                  scrollToTop();
                }}
              >
                Get Started
              </Link>
            </div>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}

export default Header;